import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import badgeManager from '../utility/badgeManager';
import usePubNub from '../hooks/pubNubNotification';

const BadgeClearer = () => {
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const identity = user?.identity;
  const notificationHistoryData = useSelector((state) => state.notification.notificationHistoryData);
  
  // Initialize PubNub for notifications - this ensures notifications work immediately on app start
  // Pass null/undefined if no user identity to still initialize push notifications
  const pubnub = usePubNub(identity);
  
  // Initialize badge manager when component mounts
  useEffect(() => {
    const initBadge = async () => {
      try {
        await badgeManager.init();
        console.log('✅ Badge manager initialized');
        
        // Check if we're on Android and can use native badge methods
        if (window.AndroidChatInfo) {
          try {
            const nativeBadgeCount = window.AndroidChatInfo.getBadgeCount();
            console.log('📱 Native Android badge count:', nativeBadgeCount);
            
            // Sync with native badge count
            if (nativeBadgeCount > 0) {
              await badgeManager.syncBadgeWithNotifications(nativeBadgeCount);
            }
          } catch (error) {
            console.log('❌ Failed to get native badge count:', error);
          }
        }
      } catch (error) {
        console.log('❌ Failed to initialize badge manager:', error);
      }
    };
    
    initBadge();
  }, []);

  useEffect(() => {
    // Clear badge when user navigates to chat-related screens
    const chatRoutes = ['/chat', '/user-chat', '/messages'];
    const isChatRoute = chatRoutes.some(route => location.pathname.includes(route));
    
    if (isChatRoute) {
      badgeManager.clearBadge();
      
      // Also clear native Android badge if available
      if (window.AndroidChatInfo) {
        try {
          window.AndroidChatInfo.clearBadgeCount();
          console.log('📱 Native Android badge cleared');
        } catch (error) {
          console.log('❌ Failed to clear native badge:', error);
        }
      }
    }
  }, [location.pathname]);
  
  // Sync badge with notification count
  useEffect(() => {
    const syncBadge = async () => {
      try {
        // Get notification count from Redux state
        const notificationCount = notificationHistoryData?.length || 0;
        const unreadCount = notificationHistoryData?.filter(n => !n.is_read)?.length || 0;
        
        console.log('🔔 Syncing badge with notifications - Total:', notificationCount, 'Unread:', unreadCount);
        
        // Use unread count for badge
        await badgeManager.syncBadgeWithNotifications(unreadCount);
        
        // Also sync with native Android badge if available
        if (window.AndroidChatInfo && unreadCount === 0) {
          try {
            window.AndroidChatInfo.clearBadgeCount();
            console.log('📱 Native Android badge cleared after sync');
          } catch (error) {
            console.log('❌ Failed to clear native badge after sync:', error);
          }
        }
      } catch (error) {
        console.log('❌ Failed to sync badge:', error);
      }
    };
    
    if (notificationHistoryData) {
      syncBadge();
    }
  }, [notificationHistoryData]);
  
  // Handle notification updates (mark as read, etc.)
  useEffect(() => {
    const handleNotificationUpdate = async () => {
      try {
        const unreadCount = notificationHistoryData?.filter(n => !n.is_read)?.length || 0;
        console.log('🔄 Notification updated, unread count:', unreadCount);
        
        // Update badge immediately when notifications change
        await badgeManager.syncBadgeWithNotifications(unreadCount);
        
        // Also sync with native Android badge
        if (window.AndroidChatInfo) {
          try {
            if (unreadCount === 0) {
              window.AndroidChatInfo.clearBadgeCount();
              console.log('📱 Native Android badge cleared after notification update');
            }
          } catch (error) {
            console.log('❌ Failed to sync native badge after notification update:', error);
          }
        }
      } catch (error) {
        console.log('❌ Failed to handle notification update:', error);
      }
    };
    
    // Debounce the update to avoid too many badge updates
    const timeoutId = setTimeout(handleNotificationUpdate, 500);
    
    return () => clearTimeout(timeoutId);
  }, [notificationHistoryData]);
  
  // Handle app state changes (foreground/background)
  useEffect(() => {
    const handleAppStateChange = async () => {
      try {
        // When app comes to foreground, refresh badge count
        if (notificationHistoryData) {
          const unreadCount = notificationHistoryData.filter(n => !n.is_read).length;
          console.log('📱 App in foreground, syncing badge count:', unreadCount);
          await badgeManager.syncBadgeWithNotifications(unreadCount);
        }
        
        // Also sync with native Android badge if available
        if (window.AndroidChatInfo) {
          try {
            const nativeBadgeCount = window.AndroidChatInfo.getBadgeCount();
            console.log('📱 Native Android badge count on foreground:', nativeBadgeCount);
            
            // If native badge count is different, sync it
            if (nativeBadgeCount > 0) {
              await badgeManager.syncBadgeWithNotifications(nativeBadgeCount);
            }
          } catch (error) {
            console.log('❌ Failed to sync with native badge on foreground:', error);
          }
        }
      } catch (error) {
        console.log('❌ Failed to handle app state change:', error);
      }
    };
    
    // Listen for visibility change (app coming to foreground)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        handleAppStateChange();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [notificationHistoryData]);

  // Check for pending chat info from notifications
  useEffect(() => {
    const checkPendingChatInfo = async () => {
      // Check localStorage first (for local notifications)
      const pendingChatInfo = localStorage.getItem('pendingChatInfo');
      if (pendingChatInfo) {
        try {
          const chatInfo = JSON.parse(pendingChatInfo);
          console.log('🔔 Found pending chat info from localStorage:', chatInfo);
          
          // Clear the pending chat info
          localStorage.removeItem('pendingChatInfo');
          
          // Navigate to the specific chat if we have channel info
          if (chatInfo.channelId && chatInfo.channelId !== `user_${identity}`) {
            const chatPath = `/chat/${chatInfo.channelId}`;
            console.log('🔔 Auto-navigating to chat:', chatPath);
            window.location.href = chatPath;
          }
          
          // Update badge count after handling notification
          setTimeout(async () => {
            try {
              const unreadCount = notificationHistoryData?.filter(n => !n.is_read)?.length || 0;
              await badgeManager.syncBadgeWithNotifications(unreadCount);
            } catch (error) {
              console.log('❌ Failed to update badge after notification click:', error);
            }
          }, 1000);
        } catch (error) {
          console.log('❌ Error parsing pending chat info from localStorage:', error);
          localStorage.removeItem('pendingChatInfo');
        }
      }

      // Check Android shared preferences (for push notifications)
      if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Preferences) {
        try {
          // Try to get from Capacitor Preferences first
          const { value } = await window.Capacitor.Plugins.Preferences.get({ key: 'pendingChatInfo' });
          if (value) {
            const chatInfo = JSON.parse(value);
            console.log('🔔 Found pending chat info from Capacitor Preferences:', chatInfo);
            
            // Clear the pending chat info
            await window.Capacitor.Plugins.Preferences.remove({ key: 'pendingChatInfo' });
            
            // Navigate to the specific chat if we have channel info
            if (chatInfo.channelId && chatInfo.channelId !== `user_${identity}`) {
              const chatPath = `/chat/${chatInfo.channelId}`;
              console.log('🔔 Auto-navigating to chat from push notification:', chatPath);
              window.location.href = chatPath;
            }
          }
        } catch (error) {
          console.log('❌ Error checking Capacitor Preferences:', error);
        }
      }
      
      // Also check if there's a custom event or data that the Android app might have set
      // This is a fallback for when the notification data is stored in a different way
      if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.App) {
        try {
          // Listen for app resume events which might indicate a notification was tapped
          window.Capacitor.Plugins.App.addListener('resume', () => {
            console.log('🔔 App resumed - checking for pending chat info...');
            // Check for pending chat info from Android JavaScript interface
            checkAndroidChatInfo();
          });
        } catch (error) {
          console.log('❌ Error setting up app resume listener:', error);
        }
      }
      
      // Check for pending chat info from Android JavaScript interface
      const checkAndroidChatInfo = () => {
        try {
          if (window.AndroidChatInfo && typeof window.AndroidChatInfo.getPendingChatInfo === 'function') {
            const chatInfo = window.AndroidChatInfo.getPendingChatInfo();
            if (chatInfo) {
              try {
                const parsedChatInfo = JSON.parse(chatInfo);
                console.log('🔔 Found pending chat info from Android interface:', parsedChatInfo);
                
                // Navigate to the specific chat if we have channel info
                if (parsedChatInfo.channelId && parsedChatInfo.channelId !== `user_${identity}`) {
                  const chatPath = `/chat/${parsedChatInfo.channelId}`;
                  console.log('🔔 Auto-navigating to chat from Android notification:', chatPath);
                  window.location.href = chatPath;
                }
              } catch (error) {
                console.log('❌ Error parsing chat info from Android interface:', error);
              }
            }
          }
        } catch (error) {
          console.log('❌ Error checking Android chat info interface:', error);
        }
      };
      
      // Check Android interface on initial load
      checkAndroidChatInfo();
    };

    checkPendingChatInfo();
  }, [identity]);

  return null; // This component doesn't render anything
};

export default BadgeClearer; 